import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Download, Upload, FileSpreadsheet, AlertCircle, CheckCircle, X, FileDown } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const CategoryCSVManager = ({ onImportSuccess }) => {
  const { toast } = useToast();
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const handleExportCSV = async (includeInactive = false) => {
    setExporting(true);
    try {
      const response = await fetch(
        `${backendUrl}/api/categories/export/csv?include_inactive=${includeInactive}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to export categories');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      const filename = filenameMatch ? filenameMatch[1].replace(/['"]/g, '') : 'categories_export.csv';

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Success',
        description: 'Categories exported successfully'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to export categories',
        variant: 'destructive'
      });
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadSample = async () => {
    try {
      const response = await fetch(
        `${backendUrl}/api/categories/export/sample-csv`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download sample');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'categories_import_sample.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: 'Downloaded',
        description: 'Sample CSV template downloaded'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to download sample',
        variant: 'destructive'
      });
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImportCSV(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImportCSV = async (file) => {
    if (!file.name.endsWith('.csv')) {
      toast({
        title: 'Invalid File',
        description: 'Please select a CSV file',
        variant: 'destructive'
      });
      return;
    }

    setImporting(true);
    setImportResults(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        `${backendUrl}/api/categories/import/csv?update_existing=${updateExisting}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to import categories');
      }

      setImportResults(data);

      if (data.success && data.imported_categories > 0) {
        toast({
          title: 'Import Successful',
          description: `Imported ${data.imported_categories} categories and ${data.imported_subcategories} subcategories`
        });
        onImportSuccess?.();
      } else if (data.errors?.length > 0) {
        toast({
          title: 'Import Completed with Errors',
          description: `${data.errors.length} errors occurred during import`,
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Import Failed',
        description: error.message || 'Failed to import categories',
        variant: 'destructive'
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            CSV Import/Export
          </CardTitle>
          <CardDescription>
            Bulk manage categories and subcategories using CSV files
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {/* Export Buttons */}
            <Button
              variant="outline"
              onClick={() => handleExportCSV(false)}
              disabled={exporting}
            >
              <Download className="h-4 w-4 mr-2" />
              {exporting ? 'Exporting...' : 'Export Active'}
            </Button>
            
            <Button
              variant="outline"
              onClick={() => handleExportCSV(true)}
              disabled={exporting}
            >
              <Download className="h-4 w-4 mr-2" />
              Export All
            </Button>

            {/* Sample Download */}
            <Button
              variant="outline"
              onClick={handleDownloadSample}
            >
              <FileDown className="h-4 w-4 mr-2" />
              Download Sample
            </Button>

            {/* Import Button */}
            <Button
              onClick={() => setShowModal(true)}
            >
              <Upload className="h-4 w-4 mr-2" />
              Import CSV
            </Button>
          </div>

          {/* Import Results */}
          {importResults && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                {importResults.success ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-yellow-500" />
                )}
                <h4 className="font-medium">Import Results</h4>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="bg-white p-3 rounded border">
                  <p className="text-gray-600">Total Rows</p>
                  <p className="text-xl font-semibold">{importResults.total_rows}</p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="text-gray-600">Categories</p>
                  <p className="text-xl font-semibold text-green-600">
                    {importResults.imported_categories}
                  </p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="text-gray-600">Subcategories</p>
                  <p className="text-xl font-semibold text-blue-600">
                    {importResults.imported_subcategories}
                  </p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="text-gray-600">Errors</p>
                  <p className="text-xl font-semibold text-red-600">
                    {importResults.errors?.length || 0}
                  </p>
                </div>
              </div>

              {/* Warnings */}
              {importResults.warnings?.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-yellow-600 mb-1">Warnings:</p>
                  <ul className="text-sm text-yellow-700 list-disc list-inside">
                    {importResults.warnings.slice(0, 5).map((warning, idx) => (
                      <li key={warning.id || warning.name || `${warning.title || "warning"}-${idx}`}>{warning}</li>
                    ))}
                    {importResults.warnings.length > 5 && (
                      <li>... and {importResults.warnings.length - 5} more</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Errors */}
              {importResults.errors?.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-red-600 mb-1">Errors:</p>
                  <ul className="text-sm text-red-700 list-disc list-inside">
                    {importResults.errors.slice(0, 5).map((error, idx) => (
                      <li key={error.id || error.name || `${error.title || "error"}-${idx}`}>{error}</li>
                    ))}
                    {importResults.errors.length > 5 && (
                      <li>... and {importResults.errors.length - 5} more</li>
                    )}
                  </ul>
                </div>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setImportResults(null)}
                className="mt-3"
              >
                Dismiss
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Import Categories from CSV</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowModal(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="p-4 space-y-4">
              <div className="bg-blue-50 p-3 rounded-lg text-sm">
                <p className="font-medium text-blue-800 mb-1">CSV Format Requirements:</p>
                <ul className="text-blue-700 list-disc list-inside space-y-1">
                  <li><code>category_name</code> and <code>category_slug</code> are required</li>
                  <li>Use pipe (|) to separate multiple listing types</li>
                  <li>Subcategory fields are optional per row</li>
                  <li>Use &quot;Yes&quot; or &quot;No&quot; for active status</li>
                </ul>
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleDownloadSample}
                  className="mt-2 p-0 h-auto text-blue-600"
                >
                  <FileDown className="h-3 w-3 mr-1" />
                  Download Sample Template
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="updateExisting"
                  checked={updateExisting}
                  onChange={(e) => setUpdateExisting(e.target.checked)}
                  className="rounded"
                />
                <label htmlFor="updateExisting" className="text-sm">
                  Update existing categories/subcategories (by slug)
                </label>
              </div>

              <div 
                className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-1">
                  Click to select a CSV file
                </p>
                <p className="text-sm text-gray-400">
                  or drag and drop
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {importing && (
                <div className="flex items-center justify-center gap-2 text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
                  Importing...
                </div>
              )}
            </div>

            <div className="p-4 border-t flex justify-end">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CategoryCSVManager;
